import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ArrowLeft, Users, Shield, Calendar, MapPin, Clock, ChevronRight,
  Megaphone, FileText, Lock, Video, ExternalLink
} from 'lucide-react';
import CommonsPrompt from '../components/CommonsPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

// Group Type Labels
const GROUP_TYPE_LABELS = {
  community: 'Community',
  activity_events: 'Activity / Events',
  learning_education: 'Learning / Education',
  support: 'Support',
  seasonal: 'Seasonal',
};

// Short, human date formatter. No relative "5 days ago" gamification — just
// calm absolute dates that respect the real-world-activity posture.
function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: sameYear ? undefined : 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatDateShort(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/**
 * Deterministic ambient backdrop for an activity that has no cover image.
 *
 * Design intent (per directive): feels like background, not design.
 * - Very soft two-stop gradient, neutral warm tones only.
 * - Tiny per-activity variation keyed off the id (barely perceptible).
 * - No patterns, no texture, no overlays, no motion.
 * If the gradient becomes visible, it is too strong.
 */
function ambientBackdrop(id) {
  let h = 0;
  const str = id || '';
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) & 0x7fffffff;
  }
  // Keep all variation within a narrow warm-neutral band.
  // Hue: 30–42 (warm paper). Saturation: 10–14. Lightness drift: 1–3%.
  const hue = 30 + (h % 13);
  const sat = 10 + ((h >> 4) % 5);
  const topL = 95 - ((h >> 8) % 2);      // 93–95
  const bottomL = 91 - ((h >> 12) % 3);  // 88–91
  const top = `hsl(${hue}, ${sat}%, ${topL}%)`;
  const bottom = `hsl(${hue + 2}, ${Math.max(sat - 2, 8)}%, ${bottomL}%)`;
  return `linear-gradient(160deg, ${top} 0%, ${bottom} 100%)`;
}

export default function GroupProfile() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [group, setGroup] = useState(null);
  const [activities, setActivities] = useState({ next: null, upcoming: [], past: [], counts: { upcoming: 0, past: 0 } });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showFullPolicy, setShowFullPolicy] = useState(false);

  useEffect(() => {
    fetchGroupProfile();
    fetchGroupActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // Handle return from Stripe Checkout: poll status and show subtle confirmation.
  useEffect(() => {
    const sessionId = searchParams.get('commons_session');
    if (!sessionId) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 6;
    const pollInterval = 2000;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(
          `${API}/api/commons/self-fund/status/${sessionId}`,
          { withCredentials: true },
        );
        if (data?.activated) {
          toast.success('This group is now Commons', {
            description: 'It will continue as long as you do.',
          });
          // Refresh group data so the Commons label picks up immediately.
          fetchGroupProfile();
          fetchGroupActivities();
          // Strip the query param so the toast doesn't re-fire.
          const next = new URLSearchParams(searchParams);
          next.delete('commons_session');
          setSearchParams(next, { replace: true });
          return;
        }
        if (data?.payment_status === 'paid') {
          // Paid but activation lagged — retry once shortly.
          setTimeout(poll, pollInterval);
          return;
        }
      } catch {
        // silent — polling is a best-effort return signal
      }
      attempts += 1;
      if (attempts < maxAttempts) setTimeout(poll, pollInterval);
    };
    poll();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchGroupProfile = async () => {
    try {
      const res = await axios.get(`${API}/api/groups/${groupId}/profile`);
      setGroup(res.data);
      setLoadError(false);
    } catch (error) {
      console.error('Error fetching group profile:', error);
      // 404 falls through to the "Group not found." whisper.
      // Any other failure surfaces as the generic "Couldn't load right now." line.
      setLoadError(error.response?.status !== 404);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupActivities = async () => {
    try {
      const res = await axios.get(`${API}/api/groups/${groupId}/activities`, { withCredentials: true });
      setActivities(res.data || { next: null, upcoming: [], past: [], counts: { upcoming: 0, past: 0 } });
    } catch {
      // silent — group still renders without activities
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div style={{ color: 'var(--fy-text-secondary)' }}>Loading...</div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
          <div className="max-w-2xl mx-auto px-4 flex items-center h-14 gap-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:opacity-70" aria-label="Back">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </button>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-8">
          <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }} data-testid="group-load-error-whisper">
            {loadError ? "Couldn't load right now. Try again in a moment." : 'Group not found.'}
          </p>
        </main>
      </div>
    );
  }

  const nextAct = activities.next;
  const moreUpcoming = activities.upcoming || [];
  const recentPast = activities.past || [];
  const isPrivate = group.visibility !== 'public';

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }} data-testid="group-profile-page">
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:opacity-70" aria-label="Back">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </button>
            <h1 className="text-sm font-semibold truncate flex-1" style={{ color: 'var(--fy-text-secondary)' }}>
              {group.name}
            </h1>
            {group.is_commons && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full font-semibold tracking-wider uppercase"
                style={{ backgroundColor: '#8B6A4F1A', color: '#6D5238', border: '1px solid #8B6A4F3D' }}
                data-testid="group-commons-label"
                title="Commons — this group continues over time"
              >
                Commons
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-8" data-testid="group-main">

        {/* 1) NEXT ACTIVITY — most prominent element on the page */}
        {nextAct ? (
          <NextActivityHero
            activity={nextAct}
            onClick={() => navigate(`/activity/${nextAct.activity_id}`)}
          />
        ) : (
          <Card
            className="p-6"
            style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px dashed var(--fy-border-subtle)' }}
            data-testid="group-next-empty"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                <Calendar className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
                  Nothing scheduled yet
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
                  When the next activity is on, it will appear here.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* 2) GROUP IDENTITY — name + simple, human description */}
        <section aria-label="About this group" data-testid="group-identity">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage-muted)' }}>
              <Users className="w-6 h-6" style={{ color: 'var(--lk-sage)' }} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-extrabold leading-tight" style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit, system-ui' }}>
                {group.name}
              </h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                <span>{GROUP_TYPE_LABELS[group.group_type] || group.group_type}</span>
                <span aria-hidden>·</span>
                <span>{group.member_count || 0} {group.member_count === 1 ? 'member' : 'members'}</span>
                {isPrivate && (
                  <>
                    <span aria-hidden>·</span>
                    <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Private</span>
                  </>
                )}
              </div>
              {group.is_commons && group.commons_funding === 'sponsor' && group.supported_by_name && (
                group.supported_by_sponsor_id ? (
                  <Link
                    to={`/support/sponsor/${group.supported_by_sponsor_id}`}
                    className="mt-2 inline-block text-xs font-medium hover:opacity-80"
                    style={{ color: '#6D5238' }}
                    data-testid="group-supported-by"
                  >
                    This group is kept going by {group.supported_by_name}
                  </Link>
                ) : (
                  <p className="mt-2 text-xs font-medium" style={{ color: '#6D5238' }} data-testid="group-supported-by">
                    This group is kept going by {group.supported_by_name}
                  </p>
                )
              )}
              {group.is_commons && group.commons_funding === 'external' && (
                <p
                  className="mt-2 text-xs font-medium inline-flex items-center gap-1"
                  style={{ color: '#6D5238' }}
                  data-testid="group-supported-by"
                >
                  {group.support_link ? (
                    <a
                      href={group.support_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 hover:opacity-80"
                      data-testid="group-support-link"
                    >
                      This group is kept going by {group.supported_by_name || 'a local donor'}
                      <ExternalLink className="w-3 h-3 opacity-60" aria-hidden="true" />
                    </a>
                  ) : (
                    <>This group is kept going by {group.supported_by_name || 'a local donor'}</>
                  )}
                </p>
              )}
              {/* Quiet "Keep this going" line — non-Commons groups, steward only.
                  Always present (subtle); the elevated CommonsPrompt below
                  remains the threshold-based heavier surface. */}
              {user && !group.is_commons && (
                group.created_by === user.user_id ||
                (group.stewards || []).some(s => s.user_id === user.user_id)
              ) && (
                <button
                  type="button"
                  onClick={() => navigate(`/commons/start?group_id=${group.group_id || groupId}`)}
                  className="mt-2 text-xs font-medium hover:underline self-start"
                  style={{ color: 'var(--fy-text-secondary)' }}
                  data-testid="group-keep-this-going"
                >
                  Keep this going
                </button>
              )}
              {group.description && (
                <p className="mt-3 text-sm leading-relaxed" style={{ color: 'var(--fy-text-primary)' }}>
                  {group.description}
                </p>
              )}
            </div>
          </div>

          {/* Steward-only Commons entry points. Hidden when group is already Commons. */}
          {user && !group.is_commons && (group.created_by === user.user_id || (group.stewards || []).some(s => s.user_id === user.user_id)) && (
            <div className="mt-4 space-y-2">
              <button
                type="button"
                onClick={() => navigate(`/support/find?group_id=${encodeURIComponent(group.group_id || groupId)}`)}
                className="w-full text-left px-4 py-3 rounded-xl text-sm font-semibold transition-opacity hover:opacity-80"
                style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px dashed var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                data-testid="group-find-support"
              >
                Find support for this group
              </button>
              <button
                type="button"
                onClick={() => navigate(`/support/external?group_id=${encodeURIComponent(group.group_id || groupId)}`)}
                className="w-full text-left px-4 py-3 rounded-xl text-sm font-semibold transition-opacity hover:opacity-80"
                style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px dashed var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                data-testid="group-external-support"
              >
                Someone is supporting this group
              </button>
            </div>
          )}
        </section>

        {/* Commons prompt — steward-only, threshold-gated, dismissible */}
        {user && <CommonsPrompt groupId={group.group_id || groupId} />}

        {/* 3) MORE FROM THIS GROUP — upcoming + past (continuity signal) */}
        {(moreUpcoming.length > 0 || recentPast.length > 0) && (
          <section aria-label="More from this group" data-testid="group-more">
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 px-1" style={{ color: 'var(--fy-text-secondary)' }}>
              More from this group
            </h3>
            <div className="space-y-2">
              {moreUpcoming.map((a) => (
                <ActivityRow
                  key={a.activity_id}
                  activity={a}
                  kind="upcoming"
                  onClick={() => navigate(`/activity/${a.activity_id}`)}
                />
              ))}
              {recentPast.map((a) => (
                <ActivityRow
                  key={a.activity_id}
                  activity={a}
                  kind="past"
                  onClick={() => navigate(`/activity/${a.activity_id}`)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Stewards — real people who hold this group */}
        {group.stewards && group.stewards.length > 0 && (
          <section aria-label="Stewards" data-testid="group-stewards">
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 px-1" style={{ color: 'var(--fy-text-secondary)' }}>
              Stewards
            </h3>
            <div className="space-y-2">
              {group.stewards.map((steward) => (
                <Link
                  key={steward.user_id}
                  to={`/profile/${steward.user_id}`}
                  className="flex items-center gap-3 p-3 rounded-xl hover:opacity-80 transition-opacity"
                  style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                >
                  <div className="w-9 h-9 rounded-full bg-slate-300 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {steward.avatar_url ? (
                      <img src={steward.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Users className="w-4 h-4 text-slate-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate" style={{ color: 'var(--fy-text-primary)' }}>
                      {steward.name || steward.username || 'Steward'}
                    </p>
                    {steward.username && (
                      <p className="text-xs truncate" style={{ color: 'var(--fy-text-secondary)' }}>
                        @{steward.username}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Promotion policy (locked, required for visibility) */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Megaphone className="w-4 h-4" style={{ color: 'var(--lk-terra)' }} />
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--fy-text-secondary)' }}>Promotion policy</h3>
          </div>
          <p className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>
            {group.promotion_policy_label}
          </p>
        </Card>

        {/* Group policy (collapsed by default) */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
              <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--fy-text-secondary)' }}>Group policy</h3>
            </div>
            <button
              onClick={() => setShowFullPolicy(!showFullPolicy)}
              className="text-xs underline"
              style={{ color: 'var(--lk-sage)' }}
            >
              {showFullPolicy ? 'Show less' : 'Show full'}
            </button>
          </div>
          {showFullPolicy ? (
            <div className="whitespace-pre-wrap text-sm leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              {group.group_policy}
            </div>
          ) : (
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              This group exists to support its stated purpose and members.
            </p>
          )}
        </Card>

        {/* Platform policy links */}
        <div className="flex flex-wrap gap-2 justify-center pt-2">
          <Link to="/about-terms" className="text-xs underline" style={{ color: 'var(--fy-text-secondary)' }}>
            Trust & Safety
          </Link>
          <span style={{ color: 'var(--fy-text-disabled)' }}>·</span>
          <Link to="/sponsor-policy" className="text-xs underline" style={{ color: 'var(--fy-text-secondary)' }}>
            Sponsorship Policy
          </Link>
        </div>
      </main>
    </div>
  );
}

/**
 * NextActivityHero — the page anchor. Larger type, calm visual weight,
 * no engagement chrome. Communicates "something real is happening."
 */
function NextActivityHero({ activity, onClick }) {
  const isVirtual = activity.activity_type === 'virtual';
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded-3xl overflow-hidden transition-transform active:scale-[0.995]"
      style={{
        backgroundColor: 'var(--fy-surface-card)',
        border: '1px solid var(--fy-border-subtle)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
      }}
      data-testid="group-next-activity"
    >
      {activity.cover_image ? (
        <div className="w-full aspect-[16/9] bg-slate-100 overflow-hidden">
          <img src={activity.cover_image} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        // Ambient backdrop — calm, deterministic per activity, barely perceptible.
        // Short band rather than full 16:9 so it feels like background, not a placeholder.
        // Keeps the hero from feeling empty without introducing content.
        <div
          className="w-full"
          style={{
            height: '72px',
            background: ambientBackdrop(activity.activity_id),
          }}
          aria-hidden="true"
          data-testid="activity-hero-ambient"
        />
      )}
      <div className="p-6">
        <p className="text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--lk-sage)' }}>
          Next — {formatDateShort(activity.start_time)}
        </p>
        <h2
          className="text-2xl font-extrabold leading-tight mb-3"
          style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit, system-ui' }}
        >
          {activity.title}
        </h2>
        <div className="space-y-1.5 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>{formatWhen(activity.start_time)}</span>
          </div>
          {isVirtual ? (
            <div className="flex items-center gap-2">
              <Video className="w-4 h-4 flex-shrink-0" />
              <span>Virtual</span>
            </div>
          ) : (
            activity.location && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{activity.location}</span>
              </div>
            )
          )}
        </div>
      </div>
    </button>
  );
}

/**
 * ActivityRow — compact, non-interactive-looking row for "More from this group".
 * No counts, no metrics, no reactions — just title + when.
 */
function ActivityRow({ activity, kind, onClick }) {
  const past = kind === 'past';
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-opacity hover:opacity-80"
      style={{
        backgroundColor: 'var(--fy-surface-card)',
        border: '1px solid var(--fy-border-subtle)',
        opacity: past ? 0.85 : 1,
      }}
      data-testid={`group-${kind}-row-${activity.activity_id}`}
    >
      <div className="w-10 h-10 rounded-lg flex-shrink-0 flex flex-col items-center justify-center text-center"
        style={{ backgroundColor: past ? 'var(--fy-surface-muted)' : 'var(--lk-sage-muted)' }}>
        <span className="text-[10px] font-semibold uppercase tracking-wider leading-none" style={{ color: past ? 'var(--fy-text-disabled)' : 'var(--lk-sage)' }}>
          {activity.start_time ? new Date(activity.start_time).toLocaleString('en-US', { month: 'short' }) : '—'}
        </span>
        <span className="text-sm font-bold leading-none mt-0.5" style={{ color: past ? 'var(--fy-text-secondary)' : 'var(--fy-text-primary)' }}>
          {activity.start_time ? new Date(activity.start_time).getDate() : ''}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate" style={{ color: past ? 'var(--fy-text-secondary)' : 'var(--fy-text-primary)' }}>
          {activity.title}
        </p>
        <p className="text-xs truncate" style={{ color: 'var(--fy-text-disabled)' }}>
          {formatWhen(activity.start_time)}
          {activity.activity_type === 'virtual' && ' · Virtual'}
        </p>
      </div>
      <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
    </button>
  );
}
